import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Sparkles, UserPlus, GitBranch, Calendar as CalendarIcon, MessagesSquare,
    StickyNote, Briefcase, Handshake, Building2, Bookmark, BookmarkCheck, X,
    ArrowRight, Reply, MessageSquarePlus, Star, Phone, ExternalLink,
    ChevronDown, Filter as FilterIcon, RefreshCw, ThumbsUp, Send, Mail,
} from "lucide-react";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import UnfitBadge from "@/components/UnfitBadge";
import MentionTextarea from "@/components/MentionTextarea";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import JobPreviewDialog from "@/components/JobPreviewDialog";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import TrackButton from "@/components/TrackButton";

/**
 * Phase 62 — Latest Updates feed.
 *
 * Social-media-style activity stream. Left sidebar has filter chips + a
 * scope toggle (All company / My jobs / Bookmarks). Each event card is
 * self-contained with:
 *   • jump-to actions (candidate, job, thread)
 *   • quick actions (reply, comment, dismiss, bookmark)
 *
 * The feed polls every 45s and also refreshes on the `pw-feed-refresh`
 * custom event so other pages can trigger updates (e.g. after a stage move).
 */

const FILTERS = [
    { id: "new_candidates", label: "New candidates", icon: UserPlus, tint: "emerald" },
    { id: "screening",      label: "Screening",       icon: GitBranch, tint: "amber" },
    { id: "interviews",     label: "Interviews",      icon: CalendarIcon, tint: "violet" },
    { id: "interactions",   label: "Interactions",    icon: MessagesSquare, tint: "sky" },
    { id: "notes",          label: "Notes & mentions", icon: StickyNote, tint: "yellow" },
    { id: "jobs",           label: "Jobs",            icon: Briefcase, tint: "blue" },
    { id: "offers",         label: "Offers",          icon: Handshake, tint: "rose" },
    { id: "clients",        label: "Clients",         icon: Building2, tint: "slate" },
    { id: "votes",          label: "Votes cast",      icon: ThumbsUp, tint: "emerald" },
];

const KIND_META = {
    candidate_new:          { label: "applied", icon: UserPlus, tint: "emerald" },
    stage_change:           { label: "stage moved", icon: GitBranch, tint: "amber" },
    rating_given:           { label: "rated", icon: Star, tint: "amber" },
    interview_scheduled:    { label: "interview scheduled", icon: CalendarIcon, tint: "violet" },
    interview_rescheduled:  { label: "rescheduled", icon: CalendarIcon, tint: "violet" },
    interview_cancelled:    { label: "cancelled", icon: CalendarIcon, tint: "rose" },
    interview_outcome:      { label: "interview outcome", icon: CalendarIcon, tint: "emerald" },
    message_sent:           { label: "message", icon: MessagesSquare, tint: "sky" },
    note_added:             { label: "note", icon: StickyNote, tint: "yellow" },
    mention_cast:           { label: "mentioned", icon: MessagesSquare, tint: "sky" },
    job_created:            { label: "job created", icon: Briefcase, tint: "blue" },
    job_updated:            { label: "job updated", icon: Briefcase, tint: "blue" },
    job_cloned:             { label: "job cloned", icon: Briefcase, tint: "blue" },
    offer_sent:             { label: "offer sent", icon: Handshake, tint: "rose" },
    offer_accepted:         { label: "offer accepted", icon: Handshake, tint: "emerald" },
    offer_declined:         { label: "offer declined", icon: Handshake, tint: "rose" },
    client_created:         { label: "new client", icon: Building2, tint: "slate" },
    followup_logged:        { label: "follow-up", icon: Phone, tint: "slate" },
    vote_cast:              { label: "vote", icon: ThumbsUp, tint: "emerald" },
    digest_shared:          { label: "digest shared", icon: Mail, tint: "blue" },
};

const TINT_CLASS = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-700",
    violet:  "border-violet-200 bg-violet-50 text-violet-700",
    sky:     "border-sky-200 bg-sky-50 text-sky-700",
    yellow:  "border-yellow-200 bg-yellow-50 text-yellow-800",
    blue:    "border-blue-200 bg-blue-50 text-blue-700",
    rose:    "border-rose-200 bg-rose-50 text-rose-700",
    slate:   "border-slate-200 bg-slate-100 text-slate-700",
};

function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const secs = Math.floor((Date.now() - then) / 1000);
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString();
}

export default function Feed() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialFilters = (searchParams.get("filters") || "").split(",").filter(Boolean);
    const [activeFilters, setActiveFilters] = useState(new Set(initialFilters));
    const [scope, setScope] = useState(searchParams.get("scope") || "all");
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [counts, setCounts] = useState({});
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    // Phase 66 — Feed quick-preview popups
    const [candPreview, setCandPreview] = useState(null);   // {candidateId, applicationId}
    const [jobPreview, setJobPreview] = useState(null);     // jobId
    const [resumePreview, setResumePreview] = useState(null); // candidateId

    const filtersCsv = useMemo(() => Array.from(activeFilters).join(","), [activeFilters]);

    const load = useCallback(async (reset = true) => {
        if (reset) setLoading(true); else setLoadingMore(true);
        try {
            const params = { scope, limit: 30 };
            if (filtersCsv) params.filters = filtersCsv;
            if (!reset && nextCursor) params.cursor = nextCursor;
            const { data } = await api.get("/feed", { params });
            setItems((prev) => reset ? (data.items || []) : [...prev, ...(data.items || [])]);
            setNextCursor(data.next_cursor || null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); setLoadingMore(false); }
    }, [scope, filtersCsv, nextCursor]);

    const loadCounts = useCallback(async () => {
        try {
            const { data } = await api.get("/feed/counts", { params: { scope } });
            setCounts(data?.counts || {});
        } catch {
            /* silent */
        }
    }, [scope]);

    useEffect(() => { load(true); loadCounts(); }, [scope, filtersCsv]);
    useEffect(() => {
        const p = new URLSearchParams();
        if (filtersCsv) p.set("filters", filtersCsv);
        if (scope !== "all") p.set("scope", scope);
        setSearchParams(p, { replace: true });
    }, [filtersCsv, scope]);

    useEffect(() => {
        // Replace the old 45s poll with a Server-Sent Events subscription.
        // When the backend detects new activity it emits a ``notify`` event
        // and we refetch the top of the feed. A safety fallback poll runs
        // every 3 minutes in case the stream drops (proxy timeout, sleep).
        const backend = (import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const streamUrl = `${backend}/api/feed/stream`;
        let es;
        let heartbeatFallback;
        try {
            es = new EventSource(streamUrl, { withCredentials: true });
            es.addEventListener("notify", () => { load(true); loadCounts(); });
            es.addEventListener("reconnect", () => { es.close(); });
            es.onerror = () => { /* browser auto-reconnects; noop */ };
        } catch {
            /* browsers without EventSource fall back to interval only */
        }
        heartbeatFallback = setInterval(() => { load(true); loadCounts(); }, 180000);
        const listener = () => { load(true); loadCounts(); };
        window.addEventListener("pw-feed-refresh", listener);
        return () => {
            if (es) es.close();
            clearInterval(heartbeatFallback);
            window.removeEventListener("pw-feed-refresh", listener);
        };
    }, [load, loadCounts]);

    const toggleFilter = (id) => {
        setActiveFilters((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const dismiss = async (evtId) => {
        setItems((arr) => arr.filter((e) => e.id !== evtId));
        try { await api.post(`/feed/dismiss/${evtId}`); } catch { /* silent */ }
    };
    const undismiss = async (evtId) => {
        setItems((arr) => arr.filter((e) => e.id !== evtId));
        try { await api.post(`/feed/undismiss/${evtId}`); toast.success("Restored"); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const toggleBookmark = async (evt) => {
        const next = !evt.bookmarked;
        setItems((arr) => arr.map((e) => e.id === evt.id ? { ...e, bookmarked: next } : e));
        try {
            if (next) await api.post(`/feed/bookmark/${evt.id}`);
            else await api.delete(`/feed/bookmark/${evt.id}`);
        } catch { setItems((arr) => arr.map((e) => e.id === evt.id ? { ...e, bookmarked: !next } : e)); }
    };

    const canScope = user?.role === "recruiter";

    return (
        <div className="max-w-7xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="feed-page">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Workspace</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Latest updates</h1>
                <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
                    Every candidate, interview, message, and hire — in one live stream. Filter, jump straight to the
                    referenced item, reply inline, and never miss a thread again.
                </p>
            </div>

            <DigestCard counts={counts} setFilters={setActiveFilters} />

            <div className="grid grid-cols-12 gap-6">
                {/* ─── Sidebar ─── */}
                <aside className="hidden lg:block col-span-3">
                    <FilterSidebar
                        activeFilters={activeFilters}
                        onToggle={toggleFilter}
                        scope={scope}
                        setScope={setScope}
                        counts={counts}
                        canScope={canScope}
                        onRefresh={() => { load(true); loadCounts(); }}
                    />
                </aside>

                {/* Mobile filter drawer trigger */}
                <div className="col-span-12 lg:hidden flex items-center gap-2">
                    <Button variant="outline" className="rounded-none h-9" onClick={() => setMobileFilterOpen((v) => !v)} data-testid="feed-mobile-filters-btn">
                        <FilterIcon className="w-4 h-4 mr-1.5" /> Filters
                        {activeFilters.size > 0 && <span className="ml-2 text-[10px] font-mono bg-ikb text-white px-1.5 py-0.5">{activeFilters.size}</span>}
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        <ScopeToggle scope={scope} setScope={setScope} canScope={canScope} />
                        <Button variant="outline" className="rounded-none h-9 w-9 p-0" onClick={() => { load(true); loadCounts(); }} data-testid="feed-refresh-mobile">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {mobileFilterOpen && (
                    <div className="col-span-12 lg:hidden">
                        <FilterSidebar
                            activeFilters={activeFilters}
                            onToggle={toggleFilter}
                            scope={scope}
                            setScope={setScope}
                            counts={counts}
                            canScope={canScope}
                            onRefresh={() => { load(true); loadCounts(); }}
                            inline
                        />
                    </div>
                )}

                {/* ─── Feed column ─── */}
                <main className="col-span-12 lg:col-span-9">
                    {loading ? (
                        <div className="text-sm text-muted-foreground font-mono">Loading feed…</div>
                    ) : items.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="space-y-3" data-testid="feed-list">
                            {items.map((evt) => (
                                <FeedCard
                                    key={evt.id}
                                    evt={evt}
                                    onNav={nav}
                                    onDismiss={() => dismiss(evt.id)}
                                    onUndismiss={() => undismiss(evt.id)}
                                    onBookmark={() => toggleBookmark(evt)}
                                    scope={scope}
                                    onAfterAction={() => { window.dispatchEvent(new CustomEvent("pw-feed-refresh")); }}
                                    onOpenCandidate={(cid, aid) => setCandPreview({ candidateId: cid, applicationId: aid })}
                                    onOpenJob={(jid) => setJobPreview(jid)}
                                    onOpenResume={(cid) => setResumePreview(cid)}
                                />
                            ))}
                            {nextCursor && (
                                <div className="pt-3">
                                    <Button
                                        variant="outline"
                                        className="rounded-none w-full h-11"
                                        onClick={() => load(false)}
                                        disabled={loadingMore}
                                        data-testid="feed-load-more"
                                    >
                                        {loadingMore ? "Loading…" : "Load older"}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Phase 66 — Feed preview modals */}
            {candPreview && (
                <CandidateProfileDialog
                    open={!!candPreview}
                    onClose={() => setCandPreview(null)}
                    candidateId={candPreview.candidateId}
                    applicationId={candPreview.applicationId}
                />
            )}
            {jobPreview && (
                <JobPreviewDialog
                    open={!!jobPreview}
                    onClose={() => setJobPreview(null)}
                    jobId={jobPreview}
                />
            )}
            {resumePreview && (
                <ResumePreviewDialog
                    open={!!resumePreview}
                    onClose={() => setResumePreview(null)}
                    candidateId={resumePreview}
                />
            )}
        </div>
    );
}

// ─────────────────── Sidebar ───────────────────
function FilterSidebar({ activeFilters, onToggle, scope, setScope, counts, canScope, onRefresh, inline }) {
    return (
        <div className={inline ? "border border-border bg-white p-4 space-y-4" : "sticky top-20 space-y-4"}>
            <div className={inline ? "" : "border border-border bg-white p-4"}>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Scope</div>
                    <button
                        type="button"
                        onClick={onRefresh}
                        className="p-1 hover:bg-secondary"
                        title="Refresh"
                        data-testid="feed-refresh-sidebar"
                    >
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                </div>
                <ScopeToggle scope={scope} setScope={setScope} canScope={canScope} full />
            </div>

            <div className={inline ? "" : "border border-border bg-white p-4"}>
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-3">Filters</div>
                <div className="space-y-1">
                    {FILTERS.map((f) => {
                        const Icon = f.icon;
                        const active = activeFilters.has(f.id);
                        const count = counts?.[f.id] || 0;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => onToggle(f.id)}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-sm border-l-2 transition-colors ${
                                    active
                                        ? "border-ikb bg-blue-50 text-ikb font-medium"
                                        : "border-transparent hover:bg-secondary text-foreground"
                                }`}
                                data-testid={`feed-filter-${f.id}`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="flex-1 text-left">{f.label}</span>
                                {count > 0 && (
                                    <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${active ? "border-ikb bg-ikb text-white" : "border-border bg-secondary text-muted-foreground"}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {activeFilters.size > 0 && (
                    <button
                        type="button"
                        onClick={() => activeFilters.forEach((f) => onToggle(f))}
                        className="w-full text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-ikb mt-3 py-1"
                        data-testid="feed-clear-filters"
                    >
                        Clear filters
                    </button>
                )}
            </div>
        </div>
    );
}

const ScopeToggle_OPTS_HAS_DISMISSED = true;
function ScopeToggle({ scope, setScope, canScope, full }) {
    const opts = [
        { id: "all", label: "All company" },
        ...(canScope ? [{ id: "mine", label: "My jobs" }] : []),
        { id: "mentions", label: "@ Me" },
        { id: "bookmarks", label: "Bookmarked" },
        { id: "dismissed", label: "Dismissed" },
    ];
    return (
        <div className={`inline-flex border border-border ${full ? "w-full flex-wrap" : ""}`} data-testid="feed-scope-toggle">
            {opts.map((o) => (
                <button
                    key={o.id}
                    type="button"
                    onClick={() => setScope(o.id)}
                    className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest flex-1 ${
                        scope === o.id ? "bg-ikb text-white" : "bg-white hover:bg-secondary"
                    }`}
                    data-testid={`feed-scope-${o.id}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}
void ScopeToggle_OPTS_HAS_DISMISSED;

function EmptyState() {
    return (
        <div className="border border-dashed border-border bg-white p-12 text-center">
            <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground">No activity yet</div>
            <div className="text-sm text-muted-foreground mt-2">
                Try widening your filters or wait for new candidates to roll in.
            </div>
        </div>
    );
}

// ─────────────────── Daily Digest ───────────────────
// Compact hero card summarizing the last 24h across all activity kinds.
// Each stat pill is a filter shortcut — clicking it sets the sidebar filter
// so the feed below narrows to that slice in one tap.
const DIGEST_STATS = [
    { id: "new_candidates", label: "New candidates",  verb: "applied",    icon: UserPlus,    tint: "emerald" },
    { id: "interviews",     label: "Interviews",      verb: "scheduled",  icon: CalendarIcon, tint: "violet" },
    { id: "screening",      label: "Stage moves",     verb: "in pipeline", icon: GitBranch,   tint: "amber" },
    { id: "interactions",   label: "Messages",        verb: "exchanged",  icon: MessagesSquare, tint: "sky" },
    { id: "offers",         label: "Offers",          verb: "in play",    icon: Handshake,   tint: "rose" },
    { id: "notes",          label: "Team notes",      verb: "added",      icon: StickyNote,  tint: "yellow" },
];

const DIGEST_TINT = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    amber:   "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
    violet:  "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100",
    sky:     "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100",
    yellow:  "border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
    rose:    "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100",
};

function DigestCard({ counts, setFilters }) {
    const [sharing, setSharing] = useState(false);
    const stats = DIGEST_STATS.map((s) => ({ ...s, count: counts?.[s.id] || 0 }));
    const total = stats.reduce((s, x) => s + x.count, 0);
    const now = new Date();
    const day = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    const hour = now.getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

    // Build a short, human sentence like: "12 new candidates, 3 interviews scheduled, 1 offer in play."
    const parts = stats.filter((s) => s.count > 0).map((s) => `${s.count} ${s.label.toLowerCase()}`);
    const sentence = parts.length ? parts.join(" · ") : "Nothing new in the last 24 hours — enjoy the quiet.";

    const share = async () => {
        if (sharing) return;
        setSharing(true);
        try {
            const { data } = await api.post("/digest/send");
            const emailed = (data?.recipients || []).length;
            toast.success(emailed > 0
                ? `Digest emailed to ${emailed} teammate${emailed === 1 ? "" : "s"} and posted to the feed`
                : "Digest posted to the feed (email skipped — Resend not configured)");
            window.dispatchEvent(new CustomEvent("pw-feed-refresh"));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSharing(false); }
    };

    return (
        <section
            className="border border-ikb/20 bg-gradient-to-br from-ikb/[0.03] via-white to-white p-4 sm:p-5 mb-6"
            data-testid="feed-digest-card"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Today · {day}</div>
                    <h2 className="font-display font-bold text-lg sm:text-xl mt-0.5">
                        {greeting} · {total > 0 ? `${total} things happened in the last 24h` : "All quiet"}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="feed-digest-sentence">
                        {sentence}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        onClick={share}
                        disabled={sharing}
                        className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white"
                        data-testid="feed-digest-share"
                    >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {sharing ? "Sending…" : "Share with team"}
                    </Button>
                    {total > 0 && (
                        <button
                            type="button"
                            onClick={() => setFilters(new Set())}
                            className="text-[11px] font-mono uppercase tracking-widest text-ikb hover:underline"
                            data-testid="feed-digest-show-all"
                        >
                            Show all →
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
                {stats.map((s) => {
                    const Icon = s.icon;
                    const active = s.count > 0;
                    return (
                        <button
                            key={s.id}
                            type="button"
                            disabled={!active}
                            onClick={() => setFilters(new Set([s.id]))}
                            className={`text-left border p-2.5 transition-colors ${
                                active
                                    ? `${DIGEST_TINT[s.tint]} cursor-pointer`
                                    : "border-border bg-secondary/40 text-muted-foreground cursor-default opacity-70"
                            }`}
                            data-testid={`feed-digest-${s.id}`}
                        >
                            <div className="flex items-center gap-1.5 mb-1">
                                <Icon className="w-3.5 h-3.5" />
                                <span className="text-[9px] font-mono uppercase tracking-widest truncate">{s.label}</span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <div className="text-xl sm:text-2xl font-display font-black leading-none">{s.count}</div>
                                <div className="text-[10px] font-mono text-muted-foreground truncate">{s.verb}</div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

// ─────────────────── Feed card ───────────────────
function FeedCard({ evt, onNav, onDismiss, onUndismiss, onBookmark, onAfterAction, onOpenCandidate, onOpenJob, onOpenResume, scope }) {
    const meta = KIND_META[evt.kind] || { label: evt.kind, icon: Sparkles, tint: "slate" };
    const Icon = meta.icon;
    const [commentOpen, setCommentOpen] = useState(false);
    const [replyOpen, setReplyOpen] = useState(false);
    const isDismissedScope = scope === "dismissed";

    return (
        <article className="border border-border bg-white hover:border-foreground/20 transition-colors" data-testid={`feed-card-${evt.id}`}>
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 shrink-0 border flex items-center justify-center ${TINT_CLASS[meta.tint]}`}>
                        <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap">
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${TINT_CLASS[meta.tint]}`}>
                                {meta.label}
                            </span>
                            <span className="text-[11px] font-mono text-muted-foreground">{timeAgo(evt.created_at)} ago</span>
                            {evt.actor?.name && (
                                <span className="text-[11px] font-mono text-muted-foreground">
                                    · by <span className="text-foreground">{evt.actor.name}</span>
                                </span>
                            )}
                            <div className="ml-auto flex items-center gap-1">
                                <IconBtn
                                    active={evt.bookmarked}
                                    Icon={evt.bookmarked ? BookmarkCheck : Bookmark}
                                    onClick={onBookmark}
                                    title={evt.bookmarked ? "Bookmarked" : "Bookmark"}
                                    testid={`feed-bookmark-${evt.id}`}
                                />
                                {isDismissedScope ? (
                                    <button
                                        type="button"
                                        onClick={onUndismiss}
                                        className="p-1.5 border border-transparent hover:border-emerald-300 hover:bg-emerald-50 text-emerald-700 transition-colors text-[10px] font-mono uppercase tracking-widest"
                                        title="Restore this event to the main feed"
                                        data-testid={`feed-undismiss-${evt.id}`}
                                    >
                                        Restore
                                    </button>
                                ) : (
                                    <IconBtn Icon={X} onClick={onDismiss} title="Dismiss" testid={`feed-dismiss-${evt.id}`} />
                                )}
                            </div>
                        </div>

                        <CardBody evt={evt} onOpenComment={() => setCommentOpen(true)} />

                        <QuickActions
                            evt={evt}
                            onReply={() => setReplyOpen((v) => !v)}
                            onComment={() => setCommentOpen((v) => !v)}
                            onNav={onNav}
                            onOpenCandidate={onOpenCandidate}
                            onOpenJob={onOpenJob}
                            onOpenResume={onOpenResume}
                        />

                        {commentOpen && evt.candidate?.id && (
                            <CommentBox
                                candidateId={evt.candidate.id}
                                sourceEvent={evt}
                                onDone={() => { setCommentOpen(false); onAfterAction?.(); }}
                                onCancel={() => setCommentOpen(false)}
                            />
                        )}
                        {replyOpen && evt.kind === "message_sent" && evt.candidate?.id && (
                            <ReplyBox
                                candidateId={evt.candidate.id}
                                onDone={() => { setReplyOpen(false); onAfterAction?.(); }}
                                onCancel={() => setReplyOpen(false)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}

function CardBody({ evt, onOpenComment }) {
    const cand = evt.candidate;
    const job = evt.job;

    // B14.8 — Rich rows (interview outcome / follow-up / rating / note)
    // deserve a persistent inline reply prompt so managers can drop a
    // one-liner ("Great write-up, let's move to offer") without hunting for
    // the Comment button in the action row. The prompt is a subtle button
    // that opens the existing MentionTextarea-backed CommentBox.
    const RICH_KINDS = new Set(["interview_outcome", "followup_logged", "rating_given", "note_added"]);
    const showReplyPrompt = RICH_KINDS.has(evt.kind) && cand?.id;
    const replyPrompt = showReplyPrompt ? (
        <button
            type="button"
            onClick={onOpenComment}
            className="mt-3 w-full text-left border border-dashed border-border hover:border-ikb hover:bg-ikb/5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-ikb inline-flex items-center gap-2 transition-colors"
            data-testid={`feed-inline-reply-${evt.id}`}
        >
            <span className="w-5 h-5 inline-flex items-center justify-center bg-ikb/10 text-ikb rounded-full font-bold">@</span>
            <span>Reply · type @ to loop a teammate in</span>
        </button>
    ) : null;

    // Helper: wrap a rendered chunk with the reply prompt (kept out of the
    // return branches so we don't duplicate the block 4 times).
    const wrap = (node) => <>{node}{replyPrompt}</>;

    if (evt.kind === "candidate_new") {
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb" data-testid={`feed-cand-link-${evt.id}`}>
                        {cand?.name || cand?.email || "Someone"}
                    </Link>
                    <UnfitBadge unfit={cand?.unfit} reason={cand?.unfit_reason} />{" "}
                    <span className="text-muted-foreground"> applied to </span>
                    {job ? (
                        <Link to={`/jobs/${job.id}`} className="font-medium hover:text-ikb">{job.title}</Link>
                    ) : <span className="text-muted-foreground italic">a role</span>}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {typeof evt.match_score === "number" && (
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                            evt.match_score >= 80 ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : evt.match_score >= 50 ? "border-amber-200 bg-amber-50 text-amber-700"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                        }`} data-testid={`feed-score-${evt.id}`}>
                            <Sparkles className="w-3 h-3 inline-block -mt-0.5 mr-0.5" />AI {evt.match_score}
                        </span>
                    )}
                    {evt.stage && <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">{evt.stage}</span>}
                    {(cand?.current_title || cand?.current_company) && (
                        <span className="text-[11px] font-mono text-muted-foreground">
                            {[cand?.current_title, cand?.current_company].filter(Boolean).join(" · ")}
                        </span>
                    )}
                </div>
                {evt.ai_summary && (
                    <p className="text-sm text-foreground/80 mt-2 leading-relaxed line-clamp-3" data-testid={`feed-summary-${evt.id}`}>
                        {evt.ai_summary}
                    </p>
                )}
                {evt.application_id && (
                    <div className="mt-3">
                        <CandidateVoteBar applicationId={evt.application_id} size="sm" />
                    </div>
                )}
            </div>
        );
    }

    if (evt.kind === "vote_cast") {
        const voteLabel = {
            interested: "interested in",
            maybe: "unsure about",
            not_interested: "not interested in",
        }[evt.vote] || "voted on";
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className="font-medium">{evt.actor?.name || "Someone"}</span>
                    <span className="text-muted-foreground"> is </span>
                    <span className="font-medium">{voteLabel}</span>
                    <span className="text-muted-foreground"> </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "candidate"}</Link>
                    {job && <span className="text-muted-foreground"> for <Link to={`/jobs/${job.id}`} className="hover:text-ikb">{job.title}</Link></span>}
                </div>
                {evt.counts && (
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        <span>Team tally:</span>
                        <span className="text-emerald-700">👍 {evt.counts.interested || 0}</span>
                        <span className="text-amber-700">🤔 {evt.counts.maybe || 0}</span>
                        <span className="text-rose-700">👎 {evt.counts.not_interested || 0}</span>
                    </div>
                )}
            </div>
        );
    }

    if (evt.kind === "digest_shared") {
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className="font-medium">{evt.actor?.name || "Someone"}</span>
                    <span className="text-muted-foreground"> shared today&apos;s morning digest</span>
                    {evt.recipient_count > 0 && <span className="text-muted-foreground"> with {evt.recipient_count} teammate{evt.recipient_count === 1 ? "" : "s"}</span>}
                </div>
                {evt.sentence && (
                    <blockquote className="mt-2 border-l-2 border-ikb/40 pl-3 text-sm text-foreground/80">
                        {evt.sentence}
                    </blockquote>
                )}
            </div>
        );
    }

    if (evt.kind === "stage_change") {
        const from = evt.meta?.old_stage || evt.meta?.from_stage;
        const to = evt.meta?.stage || evt.meta?.new_stage;
        return (
            <div className="mt-2 text-sm">
                <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "Candidate"}</Link>
                <span className="text-muted-foreground"> moved {from ? `from ${from} ` : ""}to </span>
                <span className="font-medium">{to || "next stage"}</span>
                {job && <span className="text-muted-foreground"> on <Link to={`/jobs/${job.id}`} className="hover:text-ikb">{job.title}</Link></span>}
            </div>
        );
    }

    if (evt.kind === "rating_given") {
        const rec = evt.meta?.recommendation;
        const summary = evt.meta?.summary;
        const recCls = rec === "strong_hire" || rec === "hire" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
            : rec === "no_hire" || rec === "strong_no_hire" ? "border-rose-300 text-rose-700 bg-rose-50"
            : "border-border text-muted-foreground";
        return wrap(
            <div className="mt-2">
                <div className="text-sm">
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "Candidate"}</Link>
                    <span className="text-muted-foreground"> was rated </span>
                    <span className="font-medium">{evt.meta?.overall ?? "?"} / 10</span>
                    {rec && (
                        <span className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${recCls}`}>
                            {String(rec).replace(/_/g, " ")}
                        </span>
                    )}
                </div>
                {summary && (
                    <blockquote className="mt-2 border-l-2 border-ikb/40 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-3">
                        {summary}
                    </blockquote>
                )}
            </div>
        );
    }

    if (evt.kind === "interview_outcome") {
        const oc = (evt.outcome || "").toLowerCase();
        const ocCls = oc.includes("strong hire") || oc === "pass" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
            : oc.includes("no hire") || oc === "fail" ? "border-rose-300 text-rose-700 bg-rose-50"
            : oc.includes("lean hire") ? "border-blue-300 text-blue-700 bg-blue-50"
            : "border-amber-300 text-amber-700 bg-amber-50";
        return wrap(
            <div className="mt-2">
                <div className="text-sm">
                    <span className="font-medium">{evt.round_name || "Interview"}</span>
                    <span className="text-muted-foreground"> outcome for </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "candidate"}</Link>
                    {job && <span className="text-muted-foreground"> · <Link to={`/jobs/${job.id}`} className="hover:text-ikb">{job.title}</Link></span>}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {evt.outcome && (
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${ocCls}`} data-testid="feed-outcome-pill">
                            {evt.outcome}
                        </span>
                    )}
                    {evt.rating != null && (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border text-foreground/70 bg-neutral-50">
                            Rated {evt.rating}
                        </span>
                    )}
                    {evt.panel && (
                        <span className="text-[11px] text-muted-foreground truncate">
                            Panel: {Array.isArray(evt.panel) ? evt.panel.join(", ") : evt.panel}
                        </span>
                    )}
                </div>
                {evt.feedback && (
                    <blockquote className="mt-2 border-l-2 border-emerald-400/50 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-4">
                        {evt.feedback}
                    </blockquote>
                )}
            </div>
        );
    }

    if (evt.kind.startsWith("interview")) {
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className="font-medium">{evt.round_name || "Interview"}</span>
                    <span className="text-muted-foreground"> with </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "candidate"}</Link>
                    {job && <span className="text-muted-foreground"> for <Link to={`/jobs/${job.id}`} className="hover:text-ikb">{job.title}</Link></span>}
                </div>
                {evt.scheduled_at && (
                    <div className="text-[11px] font-mono text-muted-foreground mt-1">
                        <CalendarIcon className="w-3 h-3 inline-block -mt-0.5 mr-1" />
                        {new Date(evt.scheduled_at).toLocaleString()} · {evt.mode || "TBD"}
                    </div>
                )}
                {evt.panel && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                        Panel: {Array.isArray(evt.panel) ? evt.panel.join(", ") : evt.panel}
                    </div>
                )}
                {evt.notes && (
                    <blockquote className="mt-2 border-l-2 border-violet-400/50 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-3">
                        {evt.notes}
                    </blockquote>
                )}
            </div>
        );
    }

    if (evt.kind === "message_sent") {
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className="text-muted-foreground">Message on </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "thread"}</Link>
                    {evt.subject && <span className="text-muted-foreground"> · <em>{evt.subject}</em></span>}
                </div>
                {evt.body && (
                    <blockquote className="mt-2 border-l-2 border-ikb/40 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-4">
                        {evt.body}
                    </blockquote>
                )}
            </div>
        );
    }

    if (evt.kind === "note_added") {
        return wrap(
            <div className="mt-2">
                <div className="text-sm">
                    <span className="text-muted-foreground">Note on </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "candidate"}</Link>
                    {evt.pinned && <span className="ml-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 border border-amber-200 bg-amber-50 px-1.5 py-0.5">Pinned</span>}
                </div>
                {evt.body && (
                    <p className="text-sm text-foreground/80 mt-2 whitespace-pre-wrap line-clamp-3">{evt.body}</p>
                )}
            </div>
        );
    }

    if (evt.kind === "mention_cast") {
        const n = (evt.mentioned_ids || []).length;
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className="font-medium">{evt.actor?.name || "Someone"}</span>
                    <span className="text-muted-foreground"> mentioned {n} teammate{n === 1 ? "" : "s"} on </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "a candidate"}</Link>
                </div>
                {evt.excerpt && (
                    <blockquote className="mt-2 border-l-2 border-ikb/40 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-3">
                        {evt.excerpt}
                    </blockquote>
                )}
            </div>
        );
    }
    if (evt.kind.startsWith("job")) {
        return (
            <div className="mt-2 text-sm">
                <Link to={job ? `/jobs/${job.id}` : "#"} className="font-medium hover:text-ikb">{job?.title || evt.meta?.title || "A job"}</Link>
                <span className="text-muted-foreground"> was {meta_verb(evt.kind)}</span>
                {job?.status && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">{job.status}</span>}
            </div>
        );
    }

    if (evt.kind.startsWith("offer")) {
        const cur = evt.currency || "INR";
        const total = evt.total_ctc;
        const inr = total ? new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(total) : null;
        const stCls = evt.kind === "offer_accepted" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
            : evt.kind === "offer_declined" ? "border-rose-300 text-rose-700 bg-rose-50"
            : "border-amber-300 text-amber-700 bg-amber-50";
        return (
            <div className="mt-2">
                <div className="text-sm">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${stCls} mr-1.5`}>
                        {(evt.status || "sent").toUpperCase()}
                    </span>
                    <span className="font-medium">{evt.offer_title || "Offer"}</span>
                    <span className="text-muted-foreground"> for </span>
                    {cand ? <Link to={`/candidates/${cand.id}`} className="font-medium hover:text-ikb">{cand.name}</Link> : "a candidate"}
                    {(job || evt.job_title) && (
                        <span className="text-muted-foreground"> · <Link to={job ? `/jobs/${job.id}` : "#"} className="hover:text-ikb">{job?.title || evt.job_title}</Link></span>
                    )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
                    {inr && <span className="text-emerald-700 font-bold" data-testid="feed-offer-amount">{inr}</span>}
                    {evt.expiry_at && <span>Expires {new Date(evt.expiry_at).toLocaleDateString()}</span>}
                </div>
            </div>
        );
    }

    if (evt.kind === "client_created") {
        return (
            <div className="mt-2 text-sm">
                <span className="text-muted-foreground">New client </span>
                <Link to="/clients" className="font-medium hover:text-ikb">{evt.client?.name || evt.meta?.name || "client"}</Link>
                <span className="text-muted-foreground"> added by {evt.actor?.name || "you"}</span>
            </div>
        );
    }

    if (evt.kind === "followup_logged") {
        const status = evt.meta?.status;
        const note = evt.meta?.note;
        const channel = evt.meta?.channel;
        const nextAt = evt.meta?.next_action_at;
        // Colour-code status buckets so the feed row communicates outcome at
        // a glance (see FOLLOWUP_STATUSES in routers/phase10.py).
        const s = (status || "").toLowerCase();
        const statusCls = s.includes("interested") && !s.includes("not") ? "border-emerald-300 text-emerald-700 bg-emerald-50"
            : s.includes("initial contact") ? "border-emerald-300 text-emerald-700 bg-emerald-50"
            : s.includes("call back") ? "border-blue-300 text-blue-700 bg-blue-50"
            : s.includes("not interested") ? "border-rose-300 text-rose-700 bg-rose-50"
            : s ? "border-amber-300 text-amber-700 bg-amber-50"
            : "border-border text-muted-foreground";
        return wrap(
            <div className="mt-2">
                <div className="text-sm">
                    <span className="text-muted-foreground">Follow-up on </span>
                    <Link to={cand ? `/candidates/${cand.id}` : "#"} className="font-medium hover:text-ikb">{cand?.name || "candidate"}</Link>
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {status && (
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${statusCls}`} data-testid="feed-followup-status">
                            {status}
                        </span>
                    )}
                    {channel && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                            via {channel}
                        </span>
                    )}
                    {nextAt && (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-blue-300 text-blue-700 bg-blue-50 inline-flex items-center gap-1">
                            <CalendarIcon className="w-2.5 h-2.5" /> Next: {new Date(nextAt).toLocaleDateString()}
                        </span>
                    )}
                </div>
                {note && (
                    <blockquote className="mt-2 border-l-2 border-ikb/40 pl-3 text-sm text-foreground/80 whitespace-pre-wrap line-clamp-4">
                        {note}
                    </blockquote>
                )}
            </div>
        );
    }

    return <div className="mt-2 text-sm text-muted-foreground">{evt.kind}</div>;
}

function meta_verb(k) {
    if (k === "job_created") return "created";
    if (k === "job_cloned") return "cloned";
    return "updated";
}

// ─────────────────── Quick actions ───────────────────
function QuickActions({ evt, onReply, onComment, onNav, onOpenCandidate, onOpenJob, onOpenResume }) {
    const cand = evt.candidate;
    const job = evt.job;
    const btns = [];

    if (cand?.id) {
        btns.push(
            <FeedTrackAction key="track" cid={cand.id} evtId={evt.id} />
        );
        btns.push(
            <ActionPill key="preview" Icon={ArrowRight} label="Preview" onClick={() => onOpenCandidate(cand.id, evt.application_id)} testid={`feed-action-preview-${evt.id}`} />
        );
        btns.push(
            <ActionPill key="resume" Icon={ExternalLink} label="Resume" onClick={() => onOpenResume(cand.id)} testid={`feed-action-resume-${evt.id}`} />
        );
        if (evt.kind === "candidate_new" || evt.kind === "stage_change") {
            btns.push(
                <StageMove key="stage" cid={cand.id} appId={evt.application_id} label="Shortlist" newStage="Shortlist" evtId={evt.id} />
            );
            btns.push(
                <StageMove key="reject" cid={cand.id} appId={evt.application_id} label="Reject" newStage="Rejected" evtId={evt.id} />
            );
        }
    }
    if (evt.kind === "message_sent") {
        btns.push(<ActionPill key="reply" Icon={Reply} label="Reply" onClick={onReply} testid={`feed-action-reply-${evt.id}`} />);
    }
    if (evt.kind.startsWith("interview") && evt.meet_link) {
        btns.push(
            <ActionPill key="join" Icon={ExternalLink} label="Join meeting"
                onClick={() => window.open(evt.meet_link, "_blank")}
                testid={`feed-action-join-${evt.id}`}
            />
        );
    }
    if (cand?.id) {
        btns.push(<ActionPill key="comment" Icon={MessageSquarePlus} label="Comment" onClick={onComment} testid={`feed-action-comment-${evt.id}`} />);
    }
    if (job?.id) {
        btns.push(<ActionPill key="job" Icon={Briefcase} label={cand?.id ? "Peek job" : "Open job"} onClick={() => onOpenJob(job.id)} testid={`feed-action-job-${evt.id}`} />);
    }

    if (btns.length === 0) return null;
    return <div className="flex items-center gap-1.5 flex-wrap mt-3">{btns}</div>;
}

/** Small wrapper — TrackButton styled to blend with the feed's ActionPill row. */
function FeedTrackAction({ cid, evtId }) {
    return (
        <span className="inline-flex" data-testid={`feed-action-track-wrap-${evtId}`}>
            <TrackButton
                candidateId={cid}
                testId={`feed-action-track-${evtId}`}
                className="!h-[26px] !px-2.5 !text-[11px] !tracking-widest !border-border hover:!border-amber-500"
            />
        </span>
    );
}

function ActionPill({ Icon, label, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border bg-white hover:bg-ikb hover:text-white hover:border-ikb text-[11px] font-mono uppercase tracking-widest transition-colors"
            data-testid={testid}
        >
            <Icon className="w-3 h-3" /> {label}
        </button>
    );
}

function IconBtn({ Icon, active, onClick, title, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            className={`p-1.5 border transition-colors ${active ? "border-ikb bg-ikb text-white" : "border-transparent hover:border-border hover:bg-secondary"}`}
            data-testid={testid}
        >
            <Icon className="w-3.5 h-3.5" />
        </button>
    );
}

function StageMove({ cid, appId, label, newStage, evtId }) {
    const [busy, setBusy] = useState(false);
    const go = async () => {
        if (!appId) return toast.error("No application id on this event");
        setBusy(true);
        try {
            await api.patch(`/applications/${appId}`, { stage: newStage });
            toast.success(`Moved to ${newStage}`);
            window.dispatchEvent(new CustomEvent("pw-feed-refresh"));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <button
            type="button"
            onClick={go}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border bg-white hover:bg-foreground hover:text-background text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-50"
            data-testid={`feed-action-${newStage.toLowerCase()}-${evtId}`}
        >
            {label}
        </button>
    );
}

function CommentBox({ candidateId, sourceEvent, onDone, onCancel }) {
    const [body, setBody] = useState("");
    const [mentions, setMentions] = useState([]);
    const [busy, setBusy] = useState(false);
    // A short human-readable context line inserted before the body so the
    // downstream note (and any @mention notification) shows what the reply
    // was in response to — turns the feed into a real collaboration thread.
    const ctxLine = (() => {
        const e = sourceEvent;
        if (!e) return "";
        if (e.kind === "interview_outcome")   return `↪ Re: ${e.round_name || "Interview"} outcome${e.outcome ? ` · ${e.outcome}` : ""}`;
        if (e.kind === "followup_logged")     return `↪ Re: follow-up${e.meta?.status ? ` · ${e.meta.status}` : ""}`;
        if (e.kind === "rating_given")        return `↪ Re: rating${e.meta?.overall != null ? ` · ${e.meta.overall}/10` : ""}`;
        if (e.kind === "note_added")          return `↪ Re: note by ${e.actor?.name || "teammate"}`;
        return "";
    })();
    const submit = async () => {
        if (!body.trim()) return;
        setBusy(true);
        try {
            const finalBody = ctxLine ? `${ctxLine}\n\n${body.trim()}` : body.trim();
            const { data } = await api.post(`/candidates/${candidateId}/notes`, {
                body: finalBody,
                mentions: mentions.length ? mentions : undefined,
            });
            const notified = data?.notified_count ?? 0;
            toast.success(notified > 0
                ? `Reply posted · ${notified} teammate${notified === 1 ? "" : "s"} notified`
                : "Reply posted");
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid="feed-comment-box">
            {ctxLine && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 pb-2 border-b border-dashed border-border">
                    {ctxLine}
                </div>
            )}
            <MentionTextarea
                autoFocus
                value={body}
                onChange={setBody}
                onMentions={setMentions}
                testid="feed-comment-input"
            />
            <div className="flex items-center justify-between gap-2 mt-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Type <span className="text-foreground">@</span> to mention a teammate
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="rounded-none h-8" onClick={onCancel}>Cancel</Button>
                    <Button className="rounded-none h-8 bg-ikb hover:bg-[#001f7a] text-white" onClick={submit} disabled={busy || !body.trim()} data-testid="feed-comment-submit">
                        {busy ? "Posting…" : "Post reply"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ReplyBox({ candidateId, onDone, onCancel }) {
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!body.trim()) return;
        setBusy(true);
        try {
            await api.post("/messages/threads", { candidate_id: candidateId, body: body.trim() });
            toast.success("Reply sent");
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid="feed-reply-box">
            <Textarea
                autoFocus
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your reply…"
                className="rounded-none text-sm min-h-[72px]"
                data-testid="feed-reply-input"
            />
            <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" className="rounded-none h-8" onClick={onCancel}>Cancel</Button>
                <Button className="rounded-none h-8 bg-ikb hover:bg-[#001f7a] text-white" onClick={submit} disabled={busy || !body.trim()} data-testid="feed-reply-submit">
                    {busy ? "Sending…" : "Send reply"}
                </Button>
            </div>
        </div>
    );
}
